// src/services/_base/BaseCrudService.ts
import type { IFirestoreService } from '@/platform/firebase/firestore'

type Session = { tenantId?: string; yardId?: string; userId?: string }

export abstract class BaseCrudService<T extends { id?: string }> {
  constructor(
    protected readonly collection: string,
    protected readonly firestore: IFirestoreService,
    protected readonly session?: Session,
  ) {}

  async create(data: Partial<T>): Promise<string> {
    const payload = this.beforeCreate(data)
    const id = await this.firestore.add<T>(this.collection, payload as T)
    await this.afterCreate(id, payload)
    return id
  }

  async update(id: string, data: Partial<T>): Promise<void> {
    const payload = this.beforeUpdate(id, data)
    await this.firestore.updateDoc<T>(this.collection, id, payload)
    await this.afterUpdate(id, payload)
  }

  async delete(id: string): Promise<void> {
    await this.beforeDelete(id)
    await this.firestore.deleteDoc(this.collection, id)
    await this.afterDelete(id)
  }

  // ---- lifecycle hooks (override as needed) ----
  protected beforeCreate(data: Partial<T>): Partial<T> {
    const baseData = {
      ...data,
      createdAt: new Date(),
      updatedAt: new Date(),
    }

    // Inject session data if available
    if (this.session) {
      if (this.session.yardId) {
        ;(baseData as any).yardId = this.session.yardId
      }
      if (this.session.userId) {
        ;(baseData as any).userId = this.session.userId
      }
    }

    return baseData
  }

  protected afterCreate(_id: string, _data: Partial<T>): void | Promise<void> {}
  protected beforeUpdate(_id: string, data: Partial<T>): Partial<T> {
    return { ...data, updatedAt: new Date() }
  }
  protected afterUpdate(_id: string, _data: Partial<T>): void | Promise<void> {}
  protected beforeDelete(_id: string): void | Promise<void> {}
  protected afterDelete(_id: string): void | Promise<void> {}
}
